<template>
  <div class="import-csv-container">
    <div class="page-header">
      <h2>科脉会员导入</h2>
      <p class="desc">上传科脉导出的会员数据文件（CSV/Excel），自动判断会员中心是否存在，存在则更新，不存在则创建。消费统计数据由后续订单同步累计。</p>
    </div>

    <!-- 上传区域 -->
    <div class="upload-section">
      <el-upload
        ref="uploadRef"
        drag
        :auto-upload="false"
        :on-change="handleFileChange"
        :show-file-list="true"
        :limit="1"
        accept=".csv,.xls,.xlsx"
        :disabled="isRunning"
      >
        <i class="el-icon-upload"></i>
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <div class="el-upload__tip" slot="tip">
          <span v-if="isUploading">正在上传文件... {{ uploadProgress }}%</span>
          <span v-else-if="taskStatus.status === 'RUNNING'">导入处理中，请勿重复上传</span>
          <span v-else>支持 .csv / .xls / .xlsx 格式，文件首行为表头（需包含"手机"列）</span>
        </div>
      </el-upload>
    </div>

    <!-- 导入进度 -->
    <div v-if="taskId || isUploading" class="progress-section">
      <h3>{{ isUploading ? '上传进度' : '导入进度' }}</h3>

      <!-- 处理中（上传中或后端处理中）：循环动画进度条 -->
      <div v-if="isUploading || taskStatus.status === 'RUNNING'" class="loading-progress">
        <div class="progress-bar-cycling"></div>
        <div class="progress-message">{{ isUploading ? '正在上传文件...' : (taskStatus.message || '正在处理中...') }}</div>
      </div>

      <!-- 处理完成/异常：显示结果 -->
      <div v-else class="progress-result">
        <el-progress
          :percentage="taskStatus.status === 'DONE' ? 100 : 100"
          :status="taskStatus.status === 'DONE' ? 'success' : 'exception'"
          :stroke-width="20"
        ></el-progress>
        <div class="progress-info">
          <span>总计: <strong>{{ taskStatus.total }}</strong></span>
          <span>已处理: <strong>{{ taskStatus.processed }}</strong></span>
          <span class="text-success">成功: <strong>{{ taskStatus.success }}</strong></span>
          <span class="text-warning">跳过: <strong>{{ taskStatus.skip }}</strong></span>
          <span class="text-danger">失败: <strong>{{ taskStatus.fail }}</strong></span>
        </div>
        <div v-if="taskStatus.message" class="progress-message">{{ taskStatus.message }}</div>
      </div>

      <!-- 表头缺失提示 -->
      <div v-if="taskStatus.errorType === 'HEADER_MISSING'" class="header-error-card">
        <el-alert
          :title="taskStatus.message || 'CSV表头中缺少「手机」列'"
          type="error"
          :closable="false"
          show-icon
        >
          <div slot="default" style="margin-top: 8px; font-size: 13px;">
            请检查上传文件的表头是否包含<strong>「手机」</strong>列。科脉导出的 CSV/Excel 文件首行必须为表头。
          </div>
        </el-alert>
      </div>

      <!-- 数据错误：显示结果卡片和下载按钮 -->
      <div v-if="taskStatus.status === 'DONE' && (taskStatus.errorType === 'DATA_ERROR' || taskStatus.skip > 0)" class="result-card">
        <div class="result-header" :class="taskStatus.fail > 0 ? 'partial-success' : 'warning'">
          <i :class="taskStatus.fail > 0 ? 'el-icon-circle-check' : 'el-icon-warning'"></i>
          <span>{{ taskStatus.fail > 0 ? '导入完成（部分失败）' : '导入完成（部分跳过）' }}</span>
        </div>
        <div class="result-stats">
          <div class="stat-item">
            <div class="stat-value success">{{ taskStatus.success }}</div>
            <div class="stat-label">成功</div>
          </div>
          <div class="stat-item">
            <div class="stat-value warning">{{ taskStatus.skip }}</div>
            <div class="stat-label">跳过</div>
          </div>
          <div class="stat-item" v-if="taskStatus.fail > 0">
            <div class="stat-value danger">{{ taskStatus.fail }}</div>
            <div class="stat-label">失败</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{{ taskStatus.total }}</div>
            <div class="stat-label">总计</div>
          </div>
        </div>
      </div>

      <!-- 全部成功 -->
      <div v-if="taskStatus.status === 'DONE' && taskStatus.fail === 0 && taskStatus.skip === 0 && taskStatus.errorType !== 'DATA_ERROR'" class="result-card">
        <div class="result-header success">
          <i class="el-icon-circle-check"></i>
          <span>导入完成</span>
        </div>
        <div class="result-stats">
          <div class="stat-item">
            <div class="stat-value success">{{ taskStatus.success }}</div>
            <div class="stat-label">成功</div>
          </div>
          <div class="stat-item">
            <div class="stat-value warning">{{ taskStatus.skip }}</div>
            <div class="stat-label">跳过</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{{ taskStatus.total }}</div>
            <div class="stat-label">总计</div>
          </div>
        </div>
      </div>

      <!-- 失败/跳过明细 -->
      <div v-if="(taskStatus.status === 'DONE' || taskStatus.status === 'ERROR') && failErrors.length > 0" class="error-table">
        <h4>失败/跳过明细 ({{ failErrors.length }} 条)</h4>
        <el-table :data="failErrors" border size="small" max-height="300">
          <el-table-column prop="row" label="行号" width="80" align="center" />
          <el-table-column prop="phone" label="手机号" width="140" align="center" />
          <el-table-column prop="reason" label="原因" />
        </el-table>
      </div>

      <!-- 完成操作 -->
      <div v-if="taskStatus.status === 'DONE' || taskStatus.status === 'ERROR'" class="action-buttons">
        <el-button v-if="taskStatus.errorType === 'DATA_ERROR' || taskStatus.skip > 0" type="warning" icon="el-icon-download" :loading="downloadingError" @click="handleDownloadErrorFile">
          下载错误记录({{ failErrors.length }}条)
        </el-button>
        <el-button type="primary" icon="el-icon-refresh" @click="resetImport">重新导入</el-button>
        <el-button icon="el-icon-user" @click="$router.push('/member')">查看会员列表</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { importMemberFromKemai, getKemaiImportTask, getKemaiImportResult, downloadKemaiImportErrorFile } from '@/api/member'

export default {
  name: 'MemberImportCsv',
  data() {
    return {
      taskId: null,
      taskStatus: {
        total: 0,
        processed: 0,
        success: 0,
        skip: 0,
        fail: 0,
        status: '',
        errorType: '',
        message: ''
      },
      failErrors: [],
      downloadingError: false,
      isUploading: false,
      uploadProgress: 0,
      pollingTimer: null
    }
  },
  computed: {
    isRunning() {
      return this.isUploading || this.taskStatus.status === 'RUNNING'
    },
    progressPercent() {
      if (this.isUploading) return this.uploadProgress
      if (this.taskStatus.total === 0) return 0
      return Math.round((this.taskStatus.processed / this.taskStatus.total) * 100)
    },
    progressStatus() {
      if (this.isUploading) return ''
      if (this.taskStatus.status === 'DONE') return 'success'
      if (this.taskStatus.status === 'ERROR') return 'exception'
      return ''
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    async handleFileChange(file) {
      const rawFile = file.raw
      if (!rawFile) return

      const isValidType = /\.(csv|xls|xlsx)$/i.test(rawFile.name)
      if (!isValidType) {
        this.$message.error('仅支持 .csv / .xls / .xlsx 格式的文件')
        this.$refs.uploadRef.clearFiles()
        return
      }

      this.isUploading = true
      this.uploadProgress = 0

      const formData = new FormData()
      formData.append('file', rawFile)

      try {
        const res = await importMemberFromKemai(formData, (progressEvent) => {
          if (progressEvent.lengthComputable) {
            this.uploadProgress = Math.round((progressEvent.loaded / progressEvent.total) * 100)
          }
        })
        this.isUploading = false

        console.log('[importCsv] upload response:', JSON.stringify(res))

        if (!res.data || !res.data.taskId) {
          this.$message.error('导入任务提交失败: 返回数据异常')
          return
        }
        this.taskId = res.data.taskId
        console.log('[importCsv] taskId set:', this.taskId)
        this.taskStatus = { total: 0, processed: 0, success: 0, skip: 0, fail: 0, status: 'RUNNING', message: '正在处理中...' }
        this.failErrors = []
        console.log('[importCsv] calling startPolling()')
        this.startPolling()
      } catch (e) {
        this.isUploading = false
        let msg = '提交失败，请稍后重试'
        if (e.response && e.response.data && e.response.data.message) {
          msg = e.response.data.message
        } else if (e.code === 'ECONNABORTED' || (e.message && e.message.includes('timeout'))) {
          msg = '上传超时，请检查文件大小或网络状况'
        }
        this.$message.error('提交导入任务失败: ' + msg)
      } finally {
        this.$refs.uploadRef.clearFiles()
      }
    },

    startPolling() {
      console.log('[startPolling] 开始轮询, taskId:', this.taskId)
      this.stopPolling()
      // 立即查询一次，再定时轮询
      this.pollTask()
      this.pollingTimer = setInterval(() => this.pollTask(), 3000)
    },

    async pollTask() {
      console.log('[pollTask] 开始查询, taskId:', this.taskId)
      try {
        const res = await getKemaiImportTask(this.taskId)
        console.log('[pollTask] API response:', JSON.stringify(res))
        if (!res.data) {
          console.warn('[pollTask] res.data is null/undefined')
          return
        }

        // 逐个属性赋值确保Vue响应式更新
        this.taskStatus.total = res.data.total
        this.taskStatus.processed = res.data.processed
        this.taskStatus.success = res.data.success
        this.taskStatus.skip = res.data.skip || 0
        this.taskStatus.fail = res.data.fail
        this.taskStatus.status = res.data.status
        this.taskStatus.errorType = res.data.errorType
        this.taskStatus.message = res.data.message

        console.log('[pollTask] status:', this.taskStatus.status, 'total:', this.taskStatus.total)

        if (res.data.status === 'DONE' || res.data.status === 'ERROR') {
          this.stopPolling()
          if (res.data.fail > 0 || res.data.skip > 0) {
            try {
              const resultRes = await getKemaiImportResult(this.taskId)
              this.failErrors = (resultRes.data && resultRes.data.errors) || []
            } catch (e) {
              console.error('加载失败明细失败:', e)
            }
          }
        }
      } catch (e) {
        console.error('轮询进度失败:', e)
      }
    },

    stopPolling() {
      if (this.pollingTimer) {
        console.log('[stopPolling] 停止轮询')
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    resetImport() {
      this.taskId = null
      this.taskStatus = { total: 0, processed: 0, success: 0, skip: 0, fail: 0, status: '', errorType: '', message: '' }
      this.failErrors = []
      this.downloadingError = false
      this.isUploading = false
      this.uploadProgress = 0
      this.stopPolling()
    },

    async handleDownloadErrorFile() {
      this.downloadingError = true
      try {
        const res = await downloadKemaiImportErrorFile(this.taskId)
        // 从响应头获取文件名
        const disposition = res.headers['content-disposition'] || ''
        let fileName = '导入失败记录.xlsx'
        const match = disposition.match(/filename="?([^";]+)"?/)
        if (match) {
          fileName = decodeURIComponent(match[1])
        }
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = fileName
        link.click()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('下载失败: ' + (e.message || '未知错误'))
      } finally {
        this.downloadingError = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.import-csv-container {
  padding: 20px;
  .page-header {
    margin-bottom: 24px;
    h2 {
      font-size: 20px;
      margin: 0 0 8px;
    }
    .desc {
      color: #909399;
      font-size: 13px;
      margin: 0;
    }
  }
  .upload-section {
    margin-bottom: 24px;
  }
  .progress-section {
    h3 {
      font-size: 16px;
      margin: 0 0 16px;
    }
    .loading-progress {
      .progress-bar-cycling {
        height: 20px;
        border-radius: 10px;
        background: #EBEEF5;
        position: relative;
        overflow: hidden;
        &::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 30%;
          height: 100%;
          background: #409EFF;
          border-radius: 10px;
          animation: cycling-progress 2s ease-in-out infinite;
        }
      }
      .progress-message {
        margin-top: 8px;
        color: #606266;
        font-size: 13px;
        text-align: center;
      }
    }
    .progress-result {
      .progress-info {
        display: flex;
        gap: 16px;
        margin-top: 12px;
        font-size: 14px;
        span { white-space: nowrap; }
      }
      .progress-message {
        margin-top: 8px;
        color: #606266;
        font-size: 13px;
      }
    }
    .error-table {
      margin-top: 20px;
      h4 {
        font-size: 14px;
        margin: 0 0 8px;
      }
    }
    .action-buttons {
      margin-top: 20px;
    }
  }

  @keyframes cycling-progress {
    0% { left: -30%; }
    50% { left: 100%; }
    100% { left: -30%; }
  }

  .text-success { color: #67C23A; }
  .text-warning { color: #E6A23C; }
  .text-danger { color: #F56C6C; }
  .header-error-card {
    margin-top: 20px;
  }
  .result-card {
    margin-top: 20px;
    border: 1px solid #EBEEF5;
    border-radius: 8px;
    overflow: hidden;
    .result-header {
      padding: 14px 20px;
      font-size: 16px;
      font-weight: bold;
      color: #fff;
      i { margin-right: 8px; font-size: 18px; }
      &.success { background: #67C23A; }
      &.warning { background: #E6A23C; }
      &.partial-success { background: #409EFF; }
    }
    .result-stats {
      display: flex;
      justify-content: space-around;
      padding: 24px 20px;
      .stat-item {
        text-align: center;
        .stat-value {
          font-size: 32px;
          font-weight: bold;
          color: #303133;
          &.success { color: #67C23A; }
          &.warning { color: #E6A23C; }
          &.danger { color: #F56C6C; }
        }
        .stat-label {
          margin-top: 4px;
          font-size: 13px;
          color: #909399;
        }
      }
    }
    .result-hint {
      margin: 0 20px 20px;
    }
  }
}
</style>
